import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import HelpCenterEditModal from './HelpCenterEditModal';
import { buildHelpCenterPageParams } from './helpCenterConstants';

const TABLE_COLUMNS = ['標題（英語）', '創建時間', '順序', '操作'];

export default function HelpCenterPage() {
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  async function loadRecords(pageNo = 1) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.helpCenterPageQuery(buildHelpCenterPageParams(pageNo));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
  }, []);

  function openCreate() {
    setEditId(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditId(row.id);
    setEditOpen(true);
    setMessage('');
  }

  async function confirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await adminApi.deleteHelpCenter(deleteTarget.id);
      setMessage('刪除成功');
      setDeleteTarget(null);
      await loadRecords(page);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>幫助中心</h2>
        <div className="section-header__actions">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </div>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td className="cell-wrap">{row.enTitle || '未設定'}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>{row.sort ?? '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn btn-success btn-sm" onClick={() => openEdit(row)}>
                        修改
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger-outline btn-sm"
                        onClick={() => setDeleteTarget(row)}
                      >
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next)} />

      <HelpCenterEditModal
        open={editOpen}
        helpCenterId={editId}
        onClose={() => setEditOpen(false)}
        onSaved={(wasEdit) => {
          setMessage(wasEdit ? '修改成功' : '新增成功');
          loadRecords(page);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除幫助文章"
        message="確定刪除此幫助文章？"
        confirmLabel={actionLoading ? '刪除中…' : '刪除'}
        confirmDisabled={actionLoading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
